'use client';

import { useEffect, useState } from 'react';
import { useAuthSession } from '@/components/rbac/AuthSessionProvider';
import { suscribirCualquierPerfilActualizado } from '@/lib/perfil-actualizado';
import { getMisSolicitudes, getSolicitudesPendientes } from '@/lib/solicitudes';
import { UnidadPendingBadge } from './UnidadPendingBadge';

const PENDING_ESTADOS = new Set(['nueva', 'en_revision']);

/**
 * Muestra la badge de "solicitud de cambio de unidad pendiente" en el perfil,
 * SOLO si el viewer es el propio investigador o un admin. Para el resto no
 * existe (devuelve null). Determina la visibilidad en cliente:
 *  - dueño  → `GET /solicitudes/me`
 *  - admin  → `GET /solicitudes` (pendientes) filtrado por objetoId
 * Vuelve a consultar con cada aviso de perfil actualizado (`emitirPerfilActualizado`): el modal de afiliación lo
 * emite al enviar o resolver una solicitud, y sin esto la marca no aparecía hasta recargar.
 */
export function UnidadPendingBadgeGate({ idInvestigador }: { idInvestigador?: number }) {
	const { user, status } = useAuthSession();
	const [pending, setPending] = useState(false);
	const [consulta, setConsulta] = useState(0);

	useEffect(() => suscribirCualquierPerfilActualizado(() => setConsulta((n) => n + 1)), []);

	// Who may see the badge is derived on render (no state to reset): only the owner and an admin ask for it.
	const autenticado = status === 'authenticated' && user !== null && user !== undefined && Boolean(idInvestigador);
	const isAdmin = autenticado && (user.role === 'admin' || user.role === 'administrador' || Boolean(user.esAdmin));
	const isOwner = autenticado && user.idInvestigador === idInvestigador;
	const puedeVer = isAdmin || isOwner;

	useEffect(() => {
		if (!puedeVer) return;

		let active = true;
		(async () => {
			try {
				// El dueño usa su propia lista; el admin la lista de pendientes global.
				const res = isOwner ? await getMisSolicitudes() : await getSolicitudesPendientes();
				if (!active) return;
				const match = res.solicitudes.some(
					(s) =>
						s.tipoSolicitud === 'cambio_unidad' &&
						PENDING_ESTADOS.has(s.estado) &&
						s.objetoId === idInvestigador,
				);
				setPending(match);
			} catch {
				if (active) setPending(false);
			}
		})();
		return () => {
			active = false;
		};
	}, [puedeVer, isOwner, idInvestigador, consulta]);

	if (!puedeVer || !pending) return null;
	return <UnidadPendingBadge />;
}
